import React, { createContext, useContext, useEffect, useState } from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

import { isSupabaseConfigured, supabase } from '../lib/supabaseClient';
import { buildE2eStubSession, isE2eStubAuthEnabled } from './e2eStubAuth';
import { clearTodaySessionDomainLens } from '../utils/domainPreference';

interface AuthContextValue {
  session: Session | null;
  accessToken: string | null;
  loading: boolean;
  isConfigured: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isE2eStubAuthEnabled()) {
      setSession(buildE2eStubSession());
      setLoading(false);
      return;
    }

    if (!supabase) {
      setLoading(false);
      return;
    }

    let active = true;
    supabase.auth.getSession().then(({ data }: { data: { session: Session | null } }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event: AuthChangeEvent, nextSession: Session | null) => {
      setSession(nextSession);
      setLoading(false);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const signInWithGoogle = async () => {
    if (!supabase) {
      return;
    }
    const redirectTo = `${window.location.origin}/app/today`;
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
  };

  const signOut = async () => {
    if (isE2eStubAuthEnabled()) {
      const uid = session?.user?.id;
      if (uid != null) {
        clearTodaySessionDomainLens(uid);
      }
      setSession(null);
      return;
    }
    if (!supabase) {
      return;
    }
    const { data } = await supabase.auth.getSession();
    const uid = data.session?.user?.id;
    if (uid != null) {
      clearTodaySessionDomainLens(uid);
    }
    await supabase.auth.signOut();
  };

  const value: AuthContextValue = {
    session,
    accessToken: session?.access_token ?? null,
    loading,
    isConfigured: isSupabaseConfigured,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
